import React from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Button, 
  Avatar, 
  Chip, 
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/system';
import { Email, Phone, Event, CheckCircle } from '@mui/icons-material';
import { PlusCircle, UserRound } from 'lucide-react';

const StyledClientCard = styled(Card)(({ theme, highlighted, darkMode }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
  transition: 'transform 0.2s, box-shadow 0.2s',
  padding: theme.spacing(2),
  backgroundColor: darkMode ? '#333' : '#fff',
  border: highlighted 
    ? `2px solid ${darkMode ? '#6C8EFF' : '#4361EE'}`
    : darkMode 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.08)',
  borderRadius: 8,
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[4]
  }
}));

const InfoItem = styled(Box)(({ theme, darkMode }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: darkMode ? '#ddd' : theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
}));

const ClientSearchResult = ({ client, onAddKnowledge, darkMode, isMobile }) => {
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <StyledClientCard 
      highlighted={client.highlighted}
      darkMode={darkMode}
      elevation={3}
      sx={{
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 2,
        position: 'relative',
        zIndex: 1100
      }}
    >
      {/* Highlight Badge */}
      {client.highlighted && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 5,
          }}
        >
          <Tooltip title="Added to knowledge">
            <Chip
              icon={<CheckCircle fontSize="small" />}
              label="Added"
              size="small"
              color="primary"
              sx={{
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
          </Tooltip>
        </Box>
      )}

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        <Avatar sx={{ 
          width: isMobile ? 45 : 55, 
          height: isMobile ? 45 : 55, 
          bgcolor: '#4361EE',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          {getInitials(client.first_name, client.last_name)}
        </Avatar>
        
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: darkMode ? '#fff' : '#333',
              mb: 1.5
            }}
          >
            {client.name || 'Unknown Client'}
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {client.email && (
              <InfoItem darkMode={darkMode}>
                <Email fontSize="small" sx={{ color: '#4361EE' }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: darkMode ? '#ddd' : 'inherit',
                    maxWidth: isMobile ? '100%' : '300px',
                  }}
                >
                  {client.email}
                </Typography>
              </InfoItem>
            )}
            
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 1.5,
            }}>
              {client.number && (
                <InfoItem darkMode={darkMode}>
                  <Phone fontSize="small" sx={{ color: '#4361EE' }} />
                  <Typography 
                    variant="body2"
                    sx={{ color: darkMode ? '#ddd' : 'inherit' }}
                  >
                    {client.number}
                  </Typography>
                </InfoItem>
              )}
              
              {client.last_contact && (
                <InfoItem darkMode={darkMode}>
                  <Event fontSize="small" sx={{ color: '#4361EE' }} />
                  <Typography 
                    variant="body2"
                    sx={{ color: darkMode ? '#ddd' : 'inherit' }}
                  >
                    Last Contact: {new Date(client.last_contact).toLocaleDateString()}
                  </Typography>
                </InfoItem>
              )}
            </Box>
          </Box>

          <Button
            variant={client.highlighted ? "outlined" : "contained"}
            color="primary"
            onClick={() => onAddKnowledge(client)}
            disabled={client.highlighted}
            startIcon={client.highlighted ? <CheckCircle /> : <PlusCircle size={16} />}
            sx={{
              alignSelf: isMobile ? 'stretch' : 'flex-start',
              minWidth: isMobile ? '100%' : '150px',
              borderRadius: '8px',
              textTransform: 'none',
              mt: 2,
              fontWeight: 500,
              bgcolor: client.highlighted ? 'transparent' : '#4361EE',
              color: client.highlighted ? (darkMode ? '#fff' : '#4361EE') : '#fff',
              borderColor: client.highlighted ? '#4361EE' : 'transparent',
              '&:hover': {
                bgcolor: client.highlighted ? 'transparent' : '#2D3A8C',
                borderColor: client.highlighted ? '#6C8EFF' : 'transparent',
              },
            }}
          >
            {client.highlighted ? 'Added to Knowledge' : 'Add to Knowledge'}
          </Button>
        </Box>
      </Box>
    </StyledClientCard>
  );
};

export default ClientSearchResult; 